import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiClient, fetchRequestWithAuth } from "@/lib/api-client";
import type { paths } from "@/lib/api-types";
import { getApiBaseUrl } from "@/lib/config";
import { adminQueryKeys } from "./queryKeys";
import {
  createListQueryInit,
  createDetailQueryInit,
  listQueryOptions,
  detailQueryOptions,
} from "./hooks";
import type { SortOrder } from "./sort";

export type AdminEventListItem =
  paths["/api/admin/events"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

type AdminEventsListQuery = {
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortOrder: SortOrder;
  filter?: string;
};

export function useAdminEventsList(params: AdminEventsListQuery) {
  const init = createListQueryInit(params, "startsAt");
  return apiClient.useQuery("get", "/api/admin/events", init, listQueryOptions);
}

type AdminEventDetailResponse =
  paths["/api/admin/events/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

export type AdminEventDetail = AdminEventDetailResponse["event"];

export type AdminEventParticipantStats =
  paths["/api/admin/events/{id}/participant-stats"]["get"]["responses"]["200"]["content"]["application/json"];

export function useAdminEventParticipantStats(eventId: string | undefined) {
  const init = createDetailQueryInit(eventId);
  return apiClient.useQuery(
    "get",
    "/api/admin/events/{id}/participant-stats",
    init,
    detailQueryOptions(!!eventId),
  );
}

export type AdminEventParticipantGroups =
  paths["/api/admin/events/{id}/participant-groups"]["get"]["responses"]["200"]["content"]["application/json"];

export function useAdminEventParticipantGroups(eventId: string | undefined) {
  const init = createDetailQueryInit(eventId);
  return apiClient.useQuery(
    "get",
    "/api/admin/events/{id}/participant-groups",
    init,
    detailQueryOptions(!!eventId),
  );
}

export function useAdminEvent(eventId: string | undefined) {
  const init = createDetailQueryInit(eventId);
  return apiClient.useQuery(
    "get",
    "/api/admin/events/{id}",
    init,
    detailQueryOptions(!!eventId),
  );
}

export function useAdminEventUpdate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("put", "/api/admin/events/{eventId}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.detail,
      });
    },
  });
}

export function useAdminEventCreate() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/events", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.list,
      });
    },
  });
}

// Boats (for race events) - use fetch until OpenAPI types include these paths
export type AdminBoatCrewMember = {
  id: string;
  firstName: string;
  lastName: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  group?: string | null;
  photoConsent: boolean;
  consentFormParticipantDateOfBirth: string | null;
  consentFormImportantMedicalInformation: string | null;
  consentFormMedicationDetails: string | null;
  consentFormInjuriesDetails: string | null;
  consentFormDietaryRequirementsDetails: string | null;
  consentFormDisabilityDetails: string | null;
};

export type AdminBoat = {
  id: string;
  eventId: string;
  class: string;
  sailNumber: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  paymentAmountCents: number | null;
  depositAmountCents: number | null;
  balanceDueAt: string | null;
  sharedPaymentIntentStatus: string | null;
  sharedAllocatedAmountCents: number | null;
  stripePaymentIntentStatus: string | null;
  depositStripePaymentIntentStatus: string | null;
  createdAt: string;
  updatedAt: string;
  crew: AdminBoatCrewMember[];
  pricePoint?: { id: string; label: string } | null;
};

export function useAdminEventBoats(eventId: string | undefined) {
  return useQuery({
    queryKey: adminQueryKeys.events.boats(eventId ?? ""),
    queryFn: async (): Promise<{ boats: AdminBoat[] }> => {
      const res = await fetchRequestWithAuth(
        new Request(`${getApiBaseUrl()}/api/admin/events/${eventId}/boats`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }),
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(err.detail ?? res.statusText);
      }
      return (await res.json()) as { boats: AdminBoat[] };
    },
    enabled: !!eventId,
  });
}

function filenameFromContentDisposition(
  header: string | null,
  fallback: string,
): string {
  if (!header) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through
    }
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(header);
  if (quotedMatch?.[1]) return quotedMatch[1];
  const plainMatch = /filename=([^;]+)/i.exec(header);
  if (plainMatch?.[1]) return plainMatch[1].trim();
  return fallback;
}

export async function downloadEventParticipantsExport(
  eventId: string,
): Promise<void> {
  const res = await fetchRequestWithAuth(
    new Request(
      `${getApiBaseUrl()}/api/admin/events/${eventId}/participants-export`,
      { method: "GET" },
    ),
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? res.statusText);
  }
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(
    res.headers.get("Content-Disposition"),
    "participants.xlsx",
  );
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function useRejectBoat(eventId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      boatId,
      reason,
    }: {
      boatId: string;
      reason: string;
    }) => {
      const res = await fetchRequestWithAuth(
        new Request(
          `${getApiBaseUrl()}/api/admin/events/${eventId}/boats/${boatId}/reject`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
          },
        ),
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(err.detail ?? res.statusText);
      }
      return (await res.json()) as { boat: AdminBoat };
    },
    onSuccess: async () => {
      if (eventId) {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.events.boats(eventId),
        });
      }
    },
  });
}

export function useCancelBoat(eventId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      boatId,
      reason,
      refund,
    }: {
      boatId: string;
      reason: string;
      refund?: boolean;
    }) => {
      const res = await fetchRequestWithAuth(
        new Request(
          `${getApiBaseUrl()}/api/admin/events/${eventId}/boats/${boatId}/cancel`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason, refund }),
          },
        ),
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(err.detail ?? res.statusText);
      }
      return (await res.json()) as { boat: AdminBoat };
    },
    onSuccess: async () => {
      if (eventId) {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.events.boats(eventId),
        });
      }
    },
  });
}
